import { TFunction } from 'i18next';
import { Step } from 'react-joyride';

export interface OnboardingStep extends Step {
  isMenuItem?: boolean;
  menuItemId?: number;
  requiresMenuItemId?: number;
  hideCloseButton?: boolean;
  hideSkipButton?: boolean;
}

// Common styles for reusability
const COMMON_STYLES = {
  welcomeTooltip: {
    width: '400px',
    textAlign: 'center' as const,
  },
  mobileMenuSpotlight: {
    borderRadius: '8px',
    margin: '0',
    padding: '4px',
  },
  mobileMenuTooltip: {
    transform: 'translateY(-10px)',
  },
} as const;

// Step configuration
const createWelcomeStep = (t: TFunction): OnboardingStep => ({
  target: 'body',
  content: t('welcome.message', { ns: 'onboarding' }),
  title: t('welcome.title', { ns: 'onboarding' }),
  placement: 'center',
  disableBeacon: true,
  hideCloseButton: true,
  hideSkipButton: true,
  styles: {
    tooltip: COMMON_STYLES.welcomeTooltip,
  },
});

const createCompletionStep = (t: TFunction): OnboardingStep => ({
  target: 'body',
  content: t('completion.message', { ns: 'onboarding' }),
  title: t('completion.title', { ns: 'onboarding' }),
  placement: 'center',
  hideCloseButton: true,
  hideSkipButton: true,
  styles: {
    tooltip: COMMON_STYLES.welcomeTooltip,
  },
});

export const getOnboardingSteps = (
  t: TFunction,
  isMobile: boolean,
): OnboardingStep[] => {
  const steps: OnboardingStep[] = [
    createWelcomeStep(t),
    {
      target: '#add-to-wallet-button',
      content: t('descriptions.addToWallet', { ns: 'onboarding' }),
      title: '💳 ' + t('steps.addToWallet', { ns: 'onboarding' }),
      disableBeacon: true,
    },
    {
      target: '#modify-card-button',
      content: t('descriptions.modifyCard', { ns: 'onboarding' }),
      title: '✏️ ' + t('steps.modifyCard', { ns: 'onboarding' }),
    },
  ];

  // Platform-specific step
  if (isMobile) {
    steps.push({
      target: '#mobile-menu-trigger',
      content: t('descriptions.mobileMenuTrigger', { ns: 'onboarding' }),
      title: '📱 ' + t('steps.mobileMenuTrigger', { ns: 'onboarding' }),
      placement: 'top-end',
      offset: 0,
      disableScrolling: true,
      styles: {
        spotlight: COMMON_STYLES.mobileMenuSpotlight,
        tooltip: COMMON_STYLES.mobileMenuTooltip,
      },
    });
  } else {
    steps.push({
      target: '#update-card-menu',
      content: t('descriptions.cardMenu', { ns: 'onboarding' }),
      title: '🎯 ' + t('steps.cardMenu', { ns: 'onboarding' }),
      placement: 'right',
    });
  }

  // Final step
  steps.push(createCompletionStep(t));

  return steps;
};
